import { useState, useRef, useCallback } from 'react';
import Masonry from 'react-masonry-css';
import { mulberry32 } from '../../utils/seededRandom.js';
import PolaroidExpanded from './PolaroidExpanded.jsx';
import styles from './PolaroidMasonry.module.css';
// photo.src is a local WebP built by scripts/optimize-photos.mjs — ~59KB each,
// down from 2.1MB Squarespace originals. No srcSet: at 1200px on the long edge
// a single file already covers the ~385px card past 2x.

// Eagerly loading 30 of the originals meant ~65MB before a scroll; there is
// still no reason to fetch beyond the first screenful.
const EAGER_COUNT = 6;

const BREAKPOINTS = { default: 3, 900: 2, 540: 1 };

// Reduced motion check (once at module level, stable)
const prefersReducedMotion =
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// ─── helpers ─────────────────────────────────────────────────────────────────
function formatCaption(id) {
  return `N · ${id.replace('N', '').split('').join(' ')}`;
}

// Deterministic ±1° rotation per photo
function seedRotation(index) {
  const rng = mulberry32(index + 2000);
  rng(); rng(); rng();
  return rng() * 2 - 1; // −1 … +1 degrees
}

// ─── Single polaroid item ─────────────────────────────────────────────────────
function PolaroidItem({ photo, index, onSelect }) {
  const rotation = seedRotation(index);
  const itemRef  = useRef(null);
  const frameRef = useRef(null);
  const [imgLoaded, setImgLoaded] = useState(false);

  // Only reveal the frame once the image is actually ready — never before.
  // This prevents blank/shimmer polaroids from flashing at the top of the page
  // while images are still in flight.
  const revealFrame = useCallback(() => {
    const el = frameRef.current;
    if (!el || el.classList.contains(styles.revealed)) return;
    const delay = prefersReducedMotion ? 0 : (index % 4) * 45;
    setTimeout(() => { el.classList.add(styles.revealed); }, delay);
  }, [index]);

  const handleImgLoad = useCallback(() => {
    setImgLoaded(true);
    revealFrame();
  }, [revealFrame]);

  // If image fails, still reveal the frame (avoids stuck blank slots)
  const handleImgError = useCallback(() => {
    setImgLoaded(true);
    revealFrame();
  }, [revealFrame]);

  // Pass index so expanded view can navigate
  const handleClick = useCallback(() => {
    if (!itemRef.current) return;
    onSelect(photo, itemRef.current.getBoundingClientRect(), rotation, index);
  }, [photo, rotation, onSelect, index]);

  return (
    <div
      ref={itemRef}
      className={styles.item}
      style={{
        '--rot':        `${rotation}deg`,
        '--float-dur':  `${11 + (index % 6) * 1.3}s`,
        '--float-del':  `${-(index % 9) * 1.15}s`,
        '--reveal-del': `${(index % 4) * 45}ms`,
      }}
      onClick={handleClick}
    >
      <div ref={frameRef} className={styles.frame}>
        <div className={styles.photoWrap}>
          {/* Shimmer skeleton — fades out once the image is ready */}
          <div
            aria-hidden="true"
            className={`${styles.shimmer}${imgLoaded ? ` ${styles.shimmerOut}` : ''}`}
          />
          <img
            src={photo.src}
            alt=""
            className={`${styles.photo}${imgLoaded ? ` ${styles.photoIn}` : ''}`}
            loading={index < EAGER_COUNT ? 'eager' : 'lazy'}
            decoding="async"
            fetchpriority={index < 4 ? 'high' : undefined}
            onLoad={handleImgLoad}
            onError={handleImgError}
          />
        </div>
        <div className={styles.strip}>
          <span className={styles.caption}>{formatCaption(photo.id)}</span>
        </div>
      </div>
    </div>
  );
}

// ─── Gallery ──────────────────────────────────────────────────────────────────
export default function PolaroidMasonry({ photos }) {
  const [expanded, setExpanded] = useState(null);

  const handleSelect = useCallback((photo, rect, rotation, index) => {
    setExpanded({ photo, rect, rotation, index });
  }, []);

  const handleClose = useCallback(() => setExpanded(null), []);

  return (
    <>
      <Masonry
        breakpointCols={BREAKPOINTS}
        className={styles.gallery}
        columnClassName={styles.galleryColumn}
      >
        {photos.map((photo, i) => (
          <PolaroidItem
            key={photo.id}
            photo={photo}
            index={i}
            onSelect={handleSelect}
          />
        ))}
      </Masonry>

      {expanded && (
        <PolaroidExpanded
          photos={photos}
          initialIndex={expanded.index}
          photo={expanded.photo}
          originRect={expanded.rect}
          originRotation={expanded.rotation}
          onClose={handleClose}
        />
      )}
    </>
  );
}
